import type { ReactNode } from 'react';
import { Box } from '../box/box';
import { shell, sidebar, main, navItem, navItemActive, sectionLabel } from './app-shell.css';

export interface AppShellProps {
    /** The sidebar column, typically a `CollapsibleSidebar`. */
    sidebar: ReactNode;
    /** Main content region. */
    children: ReactNode;
}

/** Top-level admin layout: a fixed sidebar column beside a scrollable main region. */
export function AppShell({ sidebar: sidebarNode, children }: AppShellProps) {
    return (
        <div className={shell}>
            {sidebarNode}
            <main className={main}>{children}</main>
        </div>
    );
}

export interface CollapsibleSidebarProps {
    /** Brand / logo region, pinned to the top. */
    header?: ReactNode;
    children?: ReactNode;
    /** Account / secondary region, pinned to the bottom. */
    footer?: ReactNode;
    collapsed?: boolean;
    expandedWidth?: number;
    collapsedWidth?: number;
}

/** Vertical navigation rail that can collapse to an icon-only width. */
export function CollapsibleSidebar({
    header,
    children,
    footer,
    collapsed = false,
    expandedWidth = 264,
    collapsedWidth = 76,
}: CollapsibleSidebarProps) {
    const width = collapsed ? collapsedWidth : expandedWidth;
    return (
        <nav className={sidebar} style={{ width }} aria-label="Main navigation">
            {header ? (
                <Box padding="400" borderColor="border-card" borderBlockEndWidth="0165">
                    {header}
                </Box>
            ) : null}
            <Box flex={1} padding="200" overflowY="auto">
                {children}
            </Box>
            {footer ? (
                <Box padding="300" borderColor="border-card" borderBlockStartWidth="0165">
                    {footer}
                </Box>
            ) : null}
        </nav>
    );
}

export interface SidebarNavItemProps {
    children: ReactNode;
    icon?: ReactNode;
    active?: boolean;
    /** Rendered as an `<a>` when set, otherwise a `<button>`. */
    href?: string;
    onPress?: () => void;
}

/** Tiny uppercase group heading between sidebar nav sections. */
export function SidebarSectionLabel({ children }: { children: ReactNode }) {
    return <span className={sectionLabel}>{children}</span>;
}

/** A single navigation entry for use inside `CollapsibleSidebar`. */
export function SidebarNavItem({ children, icon, active, href, onPress }: SidebarNavItemProps) {
    const className = active ? `${navItem} ${navItemActive}` : navItem;
    const content = (
        <>
            {icon ? (
                <span aria-hidden style={{ display: 'inline-flex', flexShrink: 0 }}>
                    {icon}
                </span>
            ) : null}
            <span>{children}</span>
        </>
    );
    if (href) {
        return (
            <a className={className} href={href} aria-current={active ? 'page' : undefined}>
                {content}
            </a>
        );
    }
    return (
        <button type="button" className={className} onClick={onPress} aria-current={active ? 'page' : undefined}>
            {content}
        </button>
    );
}
